// External dependencies
import React, { FC, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Box } from '@material-ui/core';
import arrayMutators from 'final-form-arrays';
import { OnChange } from 'react-final-form-listeners';
import { FormSpy, useField, withTypes } from 'react-final-form';
import { isEmpty, isEqual, isNil, sortBy, uniq } from 'lodash';

// Shared components
import {
  AuthenticationModal,
  Button,
  DynamicValueWrapper,
  FieldTextInput,
  Form,
  TypographyWrapper,
} from '../../components';
import { ButtonVariant, InlineTextButton } from '../../components/Button/Button';
import AppContext from '../../context/AppContext';

// Hooks
import { useAppDispatch } from '../../hooks/appDispatch';
import { useShopConfig } from '../../hooks/shopConfig';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { useRouteConfiguration } from '../../hooks/useRouteConfiguration';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import { useTradeInConfig } from '../../hooks/useTradeInConfig';
import { useOwnListingVersions } from './hooks/useOwnListingVersions';
import { useItemDetailsFormSections } from '../../hooks/useItemDetailsFormSections';
import { useCashPayoutPercentage } from '../../hooks/useCashPayoutPercentage';
import { useCreditPayoutPercentage } from '../../hooks/useCreditPayoutPercentage';
import { useActivePromos } from '../../hooks/useActivePromos';

// Types
import { RequestStatus } from '../../types/requestStatus';
import { FindItemMethod, ListingItemType, OwnListing } from '../../types/sharetribe/listing';
import { ListingFlowSection } from '../../types/shopConfig/shopConfigV2';
import { Product, ShopifyProductVariant } from '../../types/shopify/product';
import { CurrentUser } from '../../types/sharetribe/currentUser';
import { UploadcareFile } from '../../types/uploadcare/file';
import {
  Currency,
  useGetPriceDropConfigBySharetribeListingId,
} from '../../types/apollo/generated/types.generated';
import { ItemDetailsFormValues, ListingFlowFormField } from './ListingFlowTypes';

// Utility functions
import { fetchOwnListings as fetchOwnListingsApiRequest } from '../../util/api';
import { convertMoneyToNumber, convertNumberToMoney } from '../../util/currency';
import {
  getOriginalShopifyProductPrice,
  getShopifyProductVariantCompareAtAndPriceAverage,
} from '../../util/listings/listing';
import { createResourceLocatorString } from '../../util/routes';
import { getShopifyProductOptionNames } from '../../util/shopifyHelpers2';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ImageSource } from '../../util/uploadcare';
import { denormalisedResponseEntities } from '../../util/data';
import { required } from '../../util/validators';
import { setAuthModalTrigger } from '../../ducks/Auth.duck';
import {
  trackPublishNewListing,
  trackSaveDraftListing,
  trackSaveExistingListing,
} from '../../util/heap';
import { buildItemDetailsListingPayload } from '../../util/listings/listingPayload';
import { showBackgroundRemovalForm, showChooseStockPhotosForm } from '../../util/refiner';

// Local components
import Condition from './Condition';
import Description from './Description';
import EditListingPageLayout from './EditListingPageLayout';
import Heading from './Heading';
import PhotosUpload, { orderSharetribeImages } from './PhotosUpload';
import Price from './Price';
import Quirks from './Quirks';
import VariantOptions, {
  CustomItemDetailsInputField,
  CustomVariantDropDownOption,
  UNKNOWN_CATEGORIES,
} from './VariantOptions';
import TradeInPrice from './TradeInPrice';
import AcknowledgementCheckbox from './AcknowledgementCheckbox';
import ItemType from './ItemType';
import InternalBrandNotes from './InternalBrandNotes';
import ChooseStockPhotos from './ChooseStockPhotos';
import ListingFlowProgressBar from './ProgressBar/ListingFlowProgressBar';

// Duck files (Redux-related)
import {
  createListingDraft,
  EditListingPageState,
  fetchListing,
  publishListing,
  resetInitialState,
  updateListing,
  updateUnsavedListingValues,
  upsertPriceDropConfig,
} from './EditListingPage.duck';

// EditListingPage-specific utils
import { isBackgroundTransparent } from '../ListingPage/SectionImages.utils';
import {
  determineAnchorPriceInitialValue,
  determinePriceDropEnabledInitialValue,
  determinePriceDropMinPriceInitialValue,
  DRAFT_ID,
  getTradeInCreditPrice,
  IS_FROM_QR_QUERY_PARAM,
  ListingPageFormIds,
  shouldDisableTradeIn,
} from './EditListingPage.utils';

// Styles
import css from './EditListingPage.module.css';
import { Conditions } from '../../shopConfig/filters/condition';
import { getRecommendedPriceForCondition } from '../../util/listingPrice';

const SUBMIT_GUEST_LISTING_AUTH_TRIGGER = 'auth-modal-submit-guest-listing-trigger';
const SAVE_DRAFT_GUEST_LISTING_AUTH_TRIGGER = 'auth-modal-save-draft-guest-listing-trigger';

const { Form: FinalForm } = withTypes<ItemDetailsFormValues>();

const YourItem: FC = () => {
  const { allowUserGeneratedTitle } = useShopConfig();
  const { currentListing } = useOwnListingVersions();
  const { isBrand } = useCurrentUserPermissions();
  const listingTitleField = useField(ListingFlowFormField.ListingTitle);
  const { title, publicData } = currentListing.attributes;
  const { findItemMethod, shopifyProduct, shopifyProductVariant } = publicData;

  const featuredImageSrc =
    shopifyProductVariant?.image?.originalSrc || shopifyProduct?.featuredImage?.originalSrc;

  const featuredImageAltText =
    shopifyProductVariant?.image?.altText ||
    shopifyProduct?.featuredImage?.altText ||
    shopifyProduct?.title ||
    '';

  const canEditTitle =
    findItemMethod === FindItemMethod.CannotFind || allowUserGeneratedTitle || isBrand;

  return (
    <>
      <Heading style={{ paddingTop: 0 }}>Your Item</Heading>
      <Box
        display="flex"
        pt={2}
        flexDirection="column"
        maxWidth="400px"
        width="100%"
        alignItems="flex-start"
      >
        {featuredImageSrc && (
          <img
            src={featuredImageSrc}
            className={css.itemImage}
            alt={`Your Item: ${featuredImageAltText}`}
          />
        )}
        {!canEditTitle && (
          <Box display="flex" alignItems="center">
            {title && (
              <TypographyWrapper variant="body1" isDynamicValue>
                {title}
              </TypographyWrapper>
            )}
          </Box>
        )}
        {canEditTitle && (
          <Box width="100%">
            <FieldTextInput
              id={ListingFlowFormField.ListingTitle}
              name={ListingFlowFormField.ListingTitle}
              placeholder="Item name"
              validate={required('Please specify a name.')}
              input={listingTitleField.input}
              meta={listingTitleField.meta}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

const ItemDetailsForm: FC = () => {
  const history = useHistory();
  const routes = useRouteConfiguration();
  const { isBrand, isTreet } = useCurrentUserPermissions();
  const {
    variantOptions = [],
    customVariantDropdownOptions = [],
    customItemDetailsInputFields = [],
    additionalPayoutSettings,
    listingFlowConfig,
    productTitleDisplayType,
    listingFlowConfig: { shouldHideMsrpOnNewListings, originalPriceSources },
    brandApprovalFields,
    resaleValueConfig,
    internationalConfig,
    returnShippingPrice,
    requiresBrandApprovalFn,
    sizeVariantOptionName,
    allSizeVariantOptionNames,
  } = useShopConfig();

  const tradeInConfig = useTradeInConfig();
  const { allowTradeInAndMarketplace, isBuyAndTradeInOnly } = useEnabledCustomerExperiences();
  const { treetId } = useContext(AppContext);
  const { currencyConfig } = useBrandCountryConfig();
  const { conditionToRecommendedDiscount, pricingRestrictions } = listingFlowConfig;
  const dispatch = useAppDispatch();
  const {
    params,
    updateListingStatus,
    createListingDraftStatus,
    fetchListingStatus,
    publishListingStatus,
    unsavedListingValues,
  } = useSelector<any>((state) => state.EditListingPage) as EditListingPageState;
  const { authModalTrigger } = useSelector<any>((state) => state.Auth) as any;

  const currentUser = useSelector<any>((state) => state.user.currentUser) as
    | CurrentUser
    | undefined;

  const { savedListing, currentListing } = useOwnListingVersions();
  const getItemDetailsFormSectionsToShow = useItemDetailsFormSections();

  const { publicData, price: initialPrice } = currentListing.attributes;

  const { data: priceDropConfigData } = useGetPriceDropConfigBySharetribeListingId({
    variables: {
      sharetribeListingId: currentListing.id?.uuid,
    },
    fetchPolicy: 'network-only', // first execution should always be network
    nextFetchPolicy: 'cache-first', // subsequent executions can read from cache
    skip: !currentListing.id?.uuid || publicData.listingItemType !== ListingItemType.Marketplace,
  });

  useEffect(
    () => () => {
      if (params) {
        dispatch(
          resetInitialState({
            slug: params.slug,
            id: DRAFT_ID,
            type: params.type,
            tab: params.tab,
          })
        );
      }
    },
    []
  );

  const {
    category: initialCategory,
    shopifyProduct: initialShopifyProduct,
    shopifyProductVariant: initialShopifyProductVariant,
    selectedVariantOptions,
    images: uploadcareImages,
    imageSource,
    stockPhotoUrls: initialStockPhotoUrls,
    payoutInfo,
    tags,
    condition: listingCondition,
    listingItemType: currentListingItemType,
    findItemMethod,
  } = publicData;

  const isPublished =
    currentListing.attributes.state && currentListing.attributes.state !== LISTING_STATE_DRAFT;

  const cashPayoutPercentage = useCashPayoutPercentage(payoutInfo?.cashPayoutPercentage);
  const creditPayoutPercentage = useCreditPayoutPercentage(
    isPublished && payoutInfo?.creditPayoutPercentage,
    currentListingItemType === ListingItemType.TradeIn
  );
  const { activePayoutCreditBoostPromo } = useActivePromos();
  const { search } = useLocation();
  const hasQRCodeQueryParam = new URLSearchParams(search).get(IS_FROM_QR_QUERY_PARAM);

  const isRelistedTradeIn = publicData.findItemMethod === FindItemMethod.TradeInUpload;

  const customVariantOptions = customVariantDropdownOptions.map((option: any) => option.name);
  const customItemDetailsKeys = customItemDetailsInputFields.map(
    (field: CustomItemDetailsInputField) => field.key
  );

  const hasNoListingImages = isEmpty(uploadcareImages) && isEmpty(currentListing?.images);
  const shouldUseImagesAPIFields = imageSource === ImageSource.Uploadcare || hasNoListingImages;

  const getVariantValuesFromFormValues = (values: any) => {
    const shopifyProductOptions = getShopifyProductOptionNames(initialShopifyProduct);

    return uniq([
      ...variantOptions,
      ...shopifyProductOptions,
      ...customVariantOptions,
      ...customItemDetailsKeys,
    ]).reduce((acc, variantOption) => {
      if (variantOption in values) {
        acc[variantOption] = values[variantOption];
      }
      return acc;
    }, {});
  };

  const getInitialVariantValuesFromListing = () => {
    const shopifyProductOptions = getShopifyProductOptionNames(initialShopifyProduct);

    // Prioritize using the variant options on the shopify product if it exists
    if (!isEmpty(shopifyProductOptions)) {
      const customVariantsToShow = customVariantDropdownOptions.filter(
        (option: CustomVariantDropDownOption) =>
          option.showForShopifyProduct && option.name !== sizeVariantOptionName
      );

      const shopifyVariantValues = shopifyProductOptions.reduce((acc, optionName) => {
        if (optionName === 'title') {
          return acc;
        }
        return {
          ...acc,
          [optionName]: selectedVariantOptions?.[optionName],
        };
      }, {});

      return {
        ...shopifyVariantValues,
        // If we persist custom variants on Shopify-backed listings, we want to show them in the initial values
        ...customVariantsToShow.reduce(
          (acc: any, option: CustomVariantDropDownOption) => ({
            ...acc,
            [option.name]: selectedVariantOptions?.[option.name],
          }),
          {}
        ),
      };
    }

    return selectedVariantOptions || [];
  };

  const redirectToPage = (listing: OwnListing) => {
    const isDraft = listing?.attributes?.state === LISTING_STATE_DRAFT;
    const isTradeInItem =
      listing?.attributes?.publicData?.listingItemType === ListingItemType.TradeIn;

    if (isDraft) {
      return;
    }

    let routeName;
    let to;

    if (isTradeInItem) {
      const queryParams = {
        newlyListedId: listing.id?.uuid,
      };
      routeName = 'ManageTradeInsPage';
      to = createResourceLocatorString(routeName, routes, {}, queryParams);
    } else {
      routeName = 'ManageListingsPage';
      to = createResourceLocatorString(routeName, routes);
    }
    history.push(to);
  };

  const getCashPayoutPercentage = () => {
    if (isRelistedTradeIn && tradeInConfig?.cashPayoutPercentageForTradeInResale != null) {
      return tradeInConfig.cashPayoutPercentageForTradeInResale;
    }
    return cashPayoutPercentage;
  };

  const formatValuesToSave = (values: ItemDetailsFormValues, listingId?: string) => {
    const {
      [ListingFlowFormField.Category]: category,
      [ListingFlowFormField.Condition]: condition,
      [ListingFlowFormField.Description]: description,
      [ListingFlowFormField.Quirks]: quirks,
      [ListingFlowFormField.Price]: price,
      [ListingFlowFormField.OriginalPrice]: originalPrice,
      [ListingFlowFormField.Images]: imagesToSave,
      [ListingFlowFormField.NumRequiredImages]: numRequiredImages,
      [ListingFlowFormField.ItemType]: listingItemType,
      [ListingFlowFormField.InternalBrandNotes]: internalBrandNotes,
      [ListingFlowFormField.FeaturedImageId]: featuredImageId,
      [ListingFlowFormField.StockPhotos]: stockPhotoUrls,
      [ListingFlowFormField.DidUseQRCode]: didUseQRCode,
      [ListingFlowFormField.ListingTitle]: title,
      [ListingFlowFormField.RecommendedPrice]: recommendedPrice,
      [ListingFlowFormField.AutomaticPriceDropEnabled]: automaticPriceDropEnabled,
      [ListingFlowFormField.AutomaticPriceDropMinPrice]: automaticPriceDropMinPrice,
      [ListingFlowFormField.AutomaticPriceDropPercentage]: automaticPriceDropPercentage,
      [ListingFlowFormField.AutomaticPriceDropAnchorPrice]: automaticPriceDropAnchorPrice,
      [ListingFlowFormField.Tags]: listingTags,
      sharetribeFeaturedImageId,
      shopifyProductVariant,
      listingFinishTime,
      listingFlowSectionToTimestamp,
    } = values;

    const variantValues = getVariantValuesFromFormValues(values);
    // strip images of linkedUuid, rely on isBackground removed for next time user edits
    const sanitizeImages = imagesToSave?.map(({ linkedUuid, ...rest }: UploadcareFile) => rest);
    const orderedImageIds =
      orderSharetribeImages(sanitizeImages || [], sharetribeFeaturedImageId)?.map(
        (img) => img.imageId || img.id
      ) || [];

    // Logic to accommodate trade-in brands pay out a flat rate per bundle, e.g. ettitude.
    const isTradeIn = listingItemType === ListingItemType.TradeIn;
    const tradeInCreditPayoutBundleAmount =
      additionalPayoutSettings?.tradeInCreditPayoutBundleAmount;
    const updatedPayoutInfo =
      !isNil(tradeInCreditPayoutBundleAmount) && isTradeIn
        ? {
            ...publicData.payoutInfo,
            // creditPayoutFixedAmount value changes based on item disputes.
            creditPayoutFixedAmount: tradeInCreditPayoutBundleAmount,
            // Bundles with flat rates can either be $0 (if all items disputed) or this value.
            tradeInCreditPayoutBundleAmount,
          }
        : {
            cashPayoutPercentage: getCashPayoutPercentage(),
            creditPayoutPercentage,
          };

    const automaticPriceDropInfo =
      listingItemType === ListingItemType.Marketplace
        ? {
            enabled: automaticPriceDropEnabled,
            minPrice: automaticPriceDropMinPrice
              ? convertMoneyToNumber(automaticPriceDropMinPrice)
              : undefined,
            percentageDrop: automaticPriceDropPercentage,
            anchorPrice: automaticPriceDropAnchorPrice
              ? convertMoneyToNumber(automaticPriceDropAnchorPrice)
              : undefined,
          }
        : undefined;
    const disableAutoApproval = currentUser?.attributes.profile.publicData?.disableAutoApproval;
    const isBrandDirect = isBrand && !isTreet;

    return buildItemDetailsListingPayload({
      listingId,
      shopifyProductVariant,
      shopifyProduct: initialShopifyProduct,
      productTitleDisplayType,
      payoutInfo: updatedPayoutInfo,
      cashPayoutPercentage: getCashPayoutPercentage(),
      isBrand: isBrandDirect,
      disableAutoApproval,
      // Defined here if users inputted it manually
      category,
      condition,
      description,
      quirks,
      price,
      ...(!shouldHideMsrpOnNewListings && { originalPrice }),
      variantValues,
      imagesToSave: sanitizeImages,
      shouldUseImagesAPIFields,
      numRequiredImages,
      listingItemType,
      internalBrandNotes,
      orderedImageIds,
      featuredImageId,
      listingFinishTime,
      listingFlowSectionToTimestamp,
      stockPhotoUrls,
      didUseQRCode,
      title,
      recommendedPrice,
      brandApprovalFields,
      automaticPriceDropInfo,
      ...(activePayoutCreditBoostPromo &&
        !isPublished && {
          payoutCreditBoostPromoId: activePayoutCreditBoostPromo?.uniquePromoId,
        }),
      tags: listingTags,
      internationalConfig,
      returnShippingPrice,
      allSizeVariantOptionNames,
      sizeVariantOptionName,
    });
  };

  // If there is a non draft listing with the same ID, this means that the listing
  // has already been published but this listing Flow ID is still in draft
  const listingHasBeenPublished = async () => {
    const response = await fetchOwnListingsApiRequest({
      params: {
        pub_listingFlowId: publicData.listingFlowId,
      },
    });
    const listingWithMatchingId = denormalisedResponseEntities(response)[0];
    if (
      listingWithMatchingId &&
      currentListing.attributes.state !== listingWithMatchingId.attributes.state &&
      currentListing.attributes.state === LISTING_STATE_DRAFT
    ) {
      return listingWithMatchingId;
    }
    return null;
  };

  const handleSaveValues = async (values: ItemDetailsFormValues) => {
    let listingId = currentListing.id?.uuid || params?.id;
    if (isEmpty(listingId) || listingId === DRAFT_ID) {
      // Create listing if one does not already exist. e.g. guest listing, so that
      // draft listing can be properly saved.
      const newDraftListing = await dispatch(createListingDraft(unsavedListingValues));

      listingId = newDraftListing.id?.uuid;

      // Update the URL to reflect the new draft so back-button works as expected.
      const currentPathParams = { ...params, id: listingId };
      const draftURI = createResourceLocatorString(
        'EditListingPage',
        routes,
        currentPathParams,
        {}
      );
      history.replace(draftURI);
    }

    const updateValues = formatValuesToSave(values, listingId);
    await dispatch(updateUnsavedListingValues(updateValues, true));
    return dispatch(updateListing(updateValues));
  };

  const handleFormSubmit = async (values: any) => {
    const {
      [ListingFlowFormField.Images]: imagesToSave,
      [ListingFlowFormField.StockPhotos]: stockPhotosFormValue,
    } = values;
    const maybeExistingListing = savedListing;

    // Check for previously published listings so a draft listing cannot overwrite
    const listingWithMatchingId = await listingHasBeenPublished();
    if (listingWithMatchingId) {
      redirectToPage(listingWithMatchingId);
      return;
    }

    const updatedImagesWithBackgroundChecked = await Promise.all(
      imagesToSave.map(async (image: any) => {
        if (!image) {
          return image;
        }

        // If image has background removed, return it
        if (!image.isImage || image?.isBackgroundRemoved) {
          return image;
        }

        // If the image background was removed by the user,
        // we should set isBackgroundRemoved to true
        const hasTransparentBackground = await isBackgroundTransparent(image.cdnUrl);
        return hasTransparentBackground
          ? {
              ...image,
              isBackgroundRemoved: true,
            }
          : image;
      })
    );

    const listingToPublish = await handleSaveValues({
      ...values,
      images: updatedImagesWithBackgroundChecked,
    });

    const listingId = listingToPublish.id.uuid;
    const publishResult = await dispatch(publishListing(listingId, requiresBrandApprovalFn));
    const {
      publishedListing,
      pgListingId,
      state: listingState,
    } = publishResult as {
      publishedListing: any;
      pgListingId: string;
      state: string;
    };
    if (pgListingId && listingState) {
      const isActive = values[ListingFlowFormField.AutomaticPriceDropEnabled];
      // If there is an existing price drop config, update it or create a new one
      // if the form value is enabled.
      if (!isEmpty(priceDropConfigData?.priceDropConfig) || isActive) {
        await dispatch(
          upsertPriceDropConfig(
            pgListingId,
            listingState,
            publishedListing?.attributes?.publicData?.automaticPriceDropInfo,
            priceDropConfigData?.priceDropConfig
          )
        );
      }
    }

    if (
      maybeExistingListing?.attributes?.state &&
      maybeExistingListing?.attributes?.state !== LISTING_STATE_DRAFT
    ) {
      trackSaveExistingListing(maybeExistingListing, publishedListing, treetId);
    } else {
      trackPublishNewListing(publishedListing, treetId);
      const { images } = publishedListing.attributes.publicData;
      showBackgroundRemovalForm(images);
      if (!isNil(stockPhotosFormValue)) {
        showChooseStockPhotosForm();
      }
    }

    redirectToPage(publishedListing);
  };

  const getOriginalPrice = (
    shopifyProduct?: Product,
    shopifyProductVariant?: ShopifyProductVariant
  ) => {
    const originalPriceValueSaved = publicData.originalPrice;
    if (originalPriceValueSaved) {
      return convertNumberToMoney(originalPriceValueSaved, currencyConfig.currency);
    }
    const price = getOriginalShopifyProductPrice(
      currencyConfig.currency as Currency,
      shopifyProduct,
      shopifyProductVariant,
      originalPriceSources
    );
    return convertNumberToMoney(price?.amount, price?.currencyCode);
  };
  const originalPrice = getOriginalPrice(initialShopifyProduct, initialShopifyProductVariant);

  const tradeInPriceRules = tradeInConfig?.tradeInPriceRulesCollection?.items || [];
  const tradeInPrice = (listingItemType: ListingItemType) =>
    listingItemType === ListingItemType.TradeIn
      ? getTradeInCreditPrice({
          treetId,
          shopifyProduct: initialShopifyProduct,
          shopifyProductVariant: initialShopifyProductVariant,
          currency: currencyConfig.currency,
          category: initialCategory,
          condition: listingCondition,
          isPaidOutOnBundle: !isNil(additionalPayoutSettings?.tradeInCreditPayoutBundleAmount),
          tradeInPriceRules,
        })
      : undefined;

  const recommendedPriceAsMoney = () => {
    if (!listingCondition) return undefined;

    let priceToUse = originalPrice;

    if (resaleValueConfig) {
      const shopifyVariantCompareAtPriceAndPriceAverage =
        getShopifyProductVariantCompareAtAndPriceAverage(initialShopifyProductVariant);
      if (shopifyVariantCompareAtPriceAndPriceAverage)
        priceToUse = convertNumberToMoney(
          shopifyVariantCompareAtPriceAndPriceAverage,
          currencyConfig.currency
        );
    }

    const recommendedPrice = getRecommendedPriceForCondition({
      price: priceToUse,
      condition: listingCondition,
      conditionToRecommendedDiscount,
      pricingRestrictions,
      tags,
      category: publicData?.category,
      resaleValueConfig,
    });

    return convertNumberToMoney(recommendedPrice, currencyConfig.currency);
  };

  const getDefaultSellingPrice = (listingItemType: ListingItemType) => {
    if (initialPrice) {
      return initialPrice;
    }
    // Do not return P2P recommended price for trade-ins
    if (listingItemType === ListingItemType.TradeIn) {
      return tradeInPrice(publicData.listingItemType);
    }
    return initialPrice || recommendedPriceAsMoney() || undefined;
  };

  const defaultSellingPrice = getDefaultSellingPrice(publicData.listingItemType);

  const getInitialAutomaticPriceDropValues = () => {
    const {
      listingFlowConfig: { automaticPriceDropPercentage: percentageDropFromContentful },
    } = useShopConfig();
    const { automaticPriceDropInfo, listingItemType } = publicData;
    if (listingItemType === ListingItemType.Marketplace) {
      const { isActive, minPrice, percentageDrop, anchorPrice } =
        priceDropConfigData?.priceDropConfig || {};
      const {
        percentageDrop: percentageDropFromListing,
        minPrice: minPriceFromListing,
        enabled: enabledOnListing,
        anchorPrice: anchorPriceFromListing,
      } = automaticPriceDropInfo || {};
      return {
        [ListingFlowFormField.AutomaticPriceDropEnabled]: determinePriceDropEnabledInitialValue(
          isActive,
          enabledOnListing,
          !!isPublished
        ),
        [ListingFlowFormField.AutomaticPriceDropMinPrice]: determinePriceDropMinPriceInitialValue(
          minPrice,
          minPriceFromListing,
          defaultSellingPrice,
          currencyConfig.currency
        ),
        [ListingFlowFormField.AutomaticPriceDropPercentage]:
          percentageDropFromListing || percentageDrop || percentageDropFromContentful || 10,
        [ListingFlowFormField.AutomaticPriceDropAnchorPrice]: determineAnchorPriceInitialValue(
          anchorPrice,
          anchorPriceFromListing,
          defaultSellingPrice,
          currencyConfig.currency
        ),
      };
    }
    return {};
  };

  const initialValues: ItemDetailsFormValues = {
    ...(!UNKNOWN_CATEGORIES.includes(publicData.category) && {
      [ListingFlowFormField.Category]: publicData.category,
    }),
    ...(!!originalPrice && {
      [ListingFlowFormField.OriginalPrice]: originalPrice,
    }),
    [ListingFlowFormField.OriginatedAsGuestListing]: publicData.originatedAsGuestListing,
    [ListingFlowFormField.Condition]: publicData.condition,
    [ListingFlowFormField.Description]: publicData.conditionInfo?.notes,
    [ListingFlowFormField.Quirks]: publicData.conditionInfo?.quirks,
    [ListingFlowFormField.Price]: defaultSellingPrice,
    [ListingFlowFormField.Images]:
      (shouldUseImagesAPIFields ? uploadcareImages : savedListing?.images) || [],
    [ListingFlowFormField.NumRequiredImages]: publicData.numRequiredImages,
    [ListingFlowFormField.AllImages]: uploadcareImages || [],
    [ListingFlowFormField.Acknowledgement]: isPublished ? ['true'] : [],
    [ListingFlowFormField.ShopifyProductVariant]: initialShopifyProductVariant,
    [ListingFlowFormField.Tags]: publicData.tags || initialShopifyProduct?.tags,
    [ListingFlowFormField.ItemType]: publicData.listingItemType, // Will be undefined if shop supports both types.
    [ListingFlowFormField.InternalBrandNotes]: publicData.internalBrandNotes,
    ...(shouldUseImagesAPIFields && {
      [ListingFlowFormField.FeaturedImageId]: publicData.featuredImageId,
    }),
    [ListingFlowFormField.FindItem]: publicData.findItemMethod,
    [ListingFlowFormField.ListingFlowSectionToTimestamp]: publicData.listingFlowSectionToTimestamp,
    [ListingFlowFormField.StockPhotos]: initialStockPhotoUrls,
    [ListingFlowFormField.DidUseQRCode]: publicData.didUseQRCode || hasQRCodeQueryParam,
    [ListingFlowFormField.ListingTitle]: currentListing.attributes.title,
    ...(!!publicData.recommendedPrice && {
      [ListingFlowFormField.RecommendedPrice]: convertNumberToMoney(
        publicData.recommendedPrice,
        currencyConfig.currency
      ),
    }),
    ...(isBrand && {
      [ListingFlowFormField.ShopifyProductVariantIdBySku]: publicData.shopifyProductVariant?.id,
    }),
    ...getInitialVariantValuesFromListing(),
    ...getInitialAutomaticPriceDropValues(),
  };

  // TODO (TREET-1881): Set submit button as disabled when shopify queries are in progress
  const buildSubmitButton = (values: any) => {
    const isLoading =
      updateListingStatus === RequestStatus.Pending ||
      fetchListingStatus === RequestStatus.Pending ||
      (!isPublished && publishListingStatus === RequestStatus.Pending);

    const isTradeInItem = values[ListingFlowFormField.ItemType] === ListingItemType.TradeIn;

    return (
      <Button
        id={isPublished ? 'submit-update-listing' : 'submit-publish-listing'}
        variant={ButtonVariant.Primary}
        disabled={isLoading}
        inProgress={isLoading}
        className={css.footerButton}
        type="submit"
        form={ListingPageFormIds.Details}
      >
        {isPublished ? 'Update Item' : isTradeInItem ? 'Add Item' : 'List Item'}
      </Button>
    );
  };

  const handleAndTrackSaveDraft = async (values: any) => {
    const savedDraftListing = await handleSaveValues(values);
    trackSaveDraftListing(savedDraftListing, treetId);
  };

  // TODO (TREET-1881): Set submit button as disabled when shopify queries are in progress
  const buildSaveDraftButton = (values: any) => {
    const isLoading = [updateListingStatus, fetchListingStatus, createListingDraftStatus].some(
      (status) => status === RequestStatus.Pending
    );

    if (isPublished) {
      return undefined;
    }

    let buttonText = 'Save Draft';
    const wasSaved =
      (updateListingStatus === RequestStatus.Success ||
        createListingDraftStatus === RequestStatus.Success) &&
      savedListing?.attributes?.publicData?.updatedAt ===
        currentListing?.attributes?.publicData?.updatedAt;

    if (wasSaved) {
      buttonText = 'Saved';
    }

    const handleSaveDraft = () => {
      if (!currentUser) {
        dispatch(setAuthModalTrigger(SAVE_DRAFT_GUEST_LISTING_AUTH_TRIGGER));
      } else {
        handleAndTrackSaveDraft(values);
      }
    };

    return (
      <Box display="flex" alignItems="center" justifyContent="center" mr={{ xs: 2, md: 3 }}>
        <InlineTextButton
          variant={ButtonVariant.Secondary}
          disabled={isLoading}
          inProgress={isLoading}
          className={css.footerButton}
          type="button"
          onClick={handleSaveDraft}
        >
          {buttonText}
        </InlineTextButton>
      </Box>
    );
  };

  const scrollToFirstError = async (errors?: { [name: string]: string }) => {
    // Need to do a small delay here so that the validation errors can finish
    // loading before we scroll to them. Otherwise, the scroll won't work on the
    // first click.
    await new Promise((resolve) => setTimeout(resolve, 200));

    if (!errors || isEmpty(errors)) {
      return null;
    }

    const allErrorElements = Object.keys(errors).map((error) => {
      const element = document.getElementById(error);
      return { element, offsetTop: element?.offsetTop };
    });

    const { element } = sortBy(allErrorElements, ['offsetTop'])[0];

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    return null;
  };

  const listingFlowSectionsToComponents = {
    [ListingFlowSection.VariantOptions]: <VariantOptions key="variantOptions" />,
    [ListingFlowSection.Condition]: <Condition key="condition" />,
    [ListingFlowSection.Quirks]: <Quirks key="quirks" />,
    [ListingFlowSection.Description]: <Description key="description" />,
    [ListingFlowSection.Photos]: <PhotosUpload key="photos" />,
    [ListingFlowSection.StockPhotos]: <ChooseStockPhotos key="stockPhotos" />,
    [ListingFlowSection.Price]: <Price key="price" />,
    [ListingFlowSection.TradeInPrice]: <TradeInPrice key="tradeInPrice" />,
    [ListingFlowSection.AcknowledgementCheckbox]: (
      <AcknowledgementCheckbox key="acknowledgementCheckbox" />
    ),
    [ListingFlowSection.InternalBrandNotes]: <InternalBrandNotes key="internalBrandNotes" />,
  };

  const updateRemoteStoreWithFormValues = (formSpyProps: {
    values: ItemDetailsFormValues;
    dirty: boolean;
    active: string | undefined;
    dirtyFields: any;
  }) => {
    const { values, dirty, active } = formSpyProps;
    // Do not update if these fields are active - leads to laggy form & unnecessary re-renders,
    // e.g. form updating after every char input in the "description" field.
    const waitUntilInactiveFields = [
      ListingFlowFormField.Description,
      ListingFlowFormField.InternalBrandNotes,
      ListingFlowFormField.Price,
      ListingFlowFormField.OriginalPrice,
      ListingFlowFormField.ListingTitle,
      ...(customItemDetailsInputFields || [])
        .filter(
          (field: CustomItemDetailsInputField) => field.type === 'text' || field.type === 'textarea'
        )
        .map((field: CustomItemDetailsInputField) => field.key),
    ];

    if (!dirty || waitUntilInactiveFields.includes(active as ListingFlowFormField)) return;
    dispatch(updateUnsavedListingValues(formatValuesToSave(values, currentListing.id?.uuid)));
  };

  return (
    <FinalForm
      initialValuesEqual={(prevInitialValues, currInitialValues) => {
        if (!currInitialValues) return false;
        // RFF's default initialValuesEqual only does a shallow comparison of fields. Here we need
        // to do a deep comparison to check for matching nested object fields, e.g. images.
        return isEqual(prevInitialValues, currInitialValues);
      }}
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      // This keeps the Acknowledgement checkbox toggled on submit when the form gets rerendered.
      keepDirtyOnReinitialize
      render={(formRenderProps) => {
        const { handleSubmit, values, form, errors } = formRenderProps;

        const { itemType } = values;
        const isTradeIn = itemType === ListingItemType.TradeIn;

        const itemDetailsFormSectionsToShow = getItemDetailsFormSectionsToShow(itemType);

        const saveDraftButton = buildSaveDraftButton(values);
        const submitButton = buildSubmitButton(values);

        const updateTradeInPrice = (
          shopifyProduct?: Product,
          shopifyProductVariant?: ShopifyProductVariant | null,
          condition?: Conditions,
          category?: string | null | undefined,
          findItemMethodForTradeIn?: FindItemMethod
        ) => {
          const price = getTradeInCreditPrice({
            treetId,
            shopifyProduct,
            shopifyProductVariant,
            currency: currencyConfig.currency,
            isPaidOutOnBundle: !isNil(additionalPayoutSettings?.tradeInCreditPayoutBundleAmount),
            ...(condition && {
              condition,
              conditionToRecommendedDiscount,
              pricingRestrictions,
              tags: values.tags,
            }),
            ...(category && {
              category,
            }),
            tradeInPriceRules,
            originalPriceSources,
            findItemMethod: findItemMethodForTradeIn,
          });
          form.change(ListingFlowFormField.Price, price);
        };

        const recordListingFinishTime = () => {
          if (!isPublished && !values[ListingFlowFormField.ListingFinishTime]) {
            const endTime = Date.now().valueOf();
            form.change(ListingFlowFormField.ListingFinishTime, endTime);
          }
        };

        const validateItemType = () => {
          if (!values[ListingFlowFormField.ItemType]) {
            (errors || {})[ListingFlowFormField.ItemType] = 'Required.';
          }
          const { tradeInAllowedFindItemMethods } = tradeInConfig;
          const isTradeInDisabled = shouldDisableTradeIn(
            tradeInAllowedFindItemMethods,
            publicData?.findItemMethod
          );
          if (isTradeIn && isTradeInDisabled) {
            (errors || {})[ListingFlowFormField.ItemType] =
              'Trade-in is not available for this item. Please choose a different item.';
          }
        };

        const validateMinPhotosForBrandUsers = () => {
          const stockPhotos = values[ListingFlowFormField.StockPhotos];
          const sellerPhotos = values[ListingFlowFormField.Images];
          const allPhotos = [...(stockPhotos || []), ...(sellerPhotos || [])];
          const message = 'At least 1 stock photo or uploaded photo is required.';

          if (
            currentUser &&
            isBrand &&
            allPhotos.length === 0 &&
            itemDetailsFormSectionsToShow.includes(ListingFlowSection.Photos)
          ) {
            if (isNil(stockPhotos)) {
              // Listings that aren't eligible to choose stock photos
              // will have an undefined stockPhotoUrls attribute.
              (errors || {})[ListingFlowFormField.Images] =
                'At least one photo is required if there is no product data/stock image';
            } else {
              (errors || {})[ListingFlowFormField.StockPhotos] = message;
              (errors || {})[ListingFlowFormField.Images] = message;
            }
          }
        };

        const validateAuthForPublish = () => {
          if (!currentUser) {
            dispatch(setAuthModalTrigger(SUBMIT_GUEST_LISTING_AUTH_TRIGGER));
            (errors || {}).authentication = 'Please log in or sign up to continue.';
          }
        };

        const onAuthSuccess = async () => {
          // Clear auth error or submit will not work.
          if (errors?.authentication) errors.authentication = undefined;

          if (currentListing.attributes.state && params?.id && params?.id !== DRAFT_ID) {
            const listing = await dispatch(fetchListing(params.id));
            if (!listing) {
              return;
            }
          }

          // check for previously published listings
          // even if the listing ID is a draft, it could have been published on a different device, so
          // we need to check for a matching listingFlowId
          const response = await fetchOwnListingsApiRequest({
            params: {
              pub_listingFlowId: publicData.listingFlowId,
            },
          });
          const listingsWithMatchingId = denormalisedResponseEntities(response);

          const match = listingsWithMatchingId.find(
            (listing: { id: any }) => listing?.id?.uuid === currentListing?.id?.uuid
          );

          // check to prevent an existing listingFlowId from being saved/published
          // this could happen if the listing is being re-published from an un-authed listing flow
          if (listingsWithMatchingId.length > 0 && !match) {
            dispatch(setAuthModalTrigger(null));
            const to = createResourceLocatorString('ManageListingsPage', routes);
            history.push(to);
            return;
          }

          if (authModalTrigger === SUBMIT_GUEST_LISTING_AUTH_TRIGGER) form.submit();

          if (authModalTrigger === SAVE_DRAFT_GUEST_LISTING_AUTH_TRIGGER)
            handleAndTrackSaveDraft(values);

          dispatch(setAuthModalTrigger(null));
        };
        return (
          <Form
            id={ListingPageFormIds.Details}
            onSubmit={(v) => {
              recordListingFinishTime();
              validateMinPhotosForBrandUsers();
              validateItemType();
              validateAuthForPublish();
              handleSubmit(v);
              scrollToFirstError(errors);
            }}
          >
            <EditListingPageLayout
              progressBar={<ListingFlowProgressBar />}
              saveDraftButton={saveDraftButton}
              submitButton={submitButton}
            >
              <AuthenticationModal
                open={authModalTrigger !== null}
                handleClose={() => dispatch(setAuthModalTrigger(null))}
                onSuccess={onAuthSuccess}
                bannerText="Sign up or log in to save your listing."
              />

              <Box mb={5} style={{ overflowAnchor: 'none' }}>
                {!isPublished &&
                  (allowTradeInAndMarketplace || (isBuyAndTradeInOnly && isBrand)) && <ItemType />}
                <YourItem />
                {(itemDetailsFormSectionsToShow as ListingFlowSection[]).map(
                  (section) => listingFlowSectionsToComponents[section]
                )}
              </Box>
            </EditListingPageLayout>
            <OnChange name={ListingFlowFormField.ShopifyProductVariant}>
              {(value, previousValue) => {
                if (!value) return;
                if (isTradeIn) {
                  updateTradeInPrice(
                    initialShopifyProduct,
                    value,
                    values.condition,
                    values.category,
                    findItemMethod
                  );
                }

                const hasOriginalPriceValueSaved =
                  savedListing?.attributes?.publicData?.originalPrice;

                // Only update the field if the existing value
                // was not informed by the product variant.
                // Tooltip will always show the original price of
                // the currently selected product variant.
                if (!previousValue?.id || !hasOriginalPriceValueSaved) {
                  const ogPrice = getOriginalPrice(initialShopifyProduct, value);
                  form.change(ListingFlowFormField.OriginalPrice, ogPrice);
                }
              }}
            </OnChange>
            <OnChange name={ListingFlowFormField.ItemType}>
              {(value) => {
                if (!value) return;

                if (value === ListingItemType.TradeIn) {
                  updateTradeInPrice(
                    initialShopifyProduct,
                    values.shopifyProductVariant,
                    values.condition,
                    values.category,
                    findItemMethod
                  );
                } else if (form.getState().dirtyFields?.[ListingFlowFormField.ItemType]) {
                  // if ListingItemType was changed from Trade In to Marketplace and form state is dirty, reset price
                  form.change(ListingFlowFormField.Price, undefined);
                }
              }}
            </OnChange>
            <OnChange name={ListingFlowFormField.Condition}>
              {(value) => {
                if (!value) return;
                if (isTradeIn)
                  updateTradeInPrice(
                    initialShopifyProduct,
                    values.shopifyProductVariant,
                    values.condition,
                    values.category,
                    findItemMethod
                  );
              }}
            </OnChange>
            <OnChange name={ListingFlowFormField.Category}>
              {(value) => {
                if (!value) return;
                if (isTradeIn)
                  updateTradeInPrice(
                    initialShopifyProduct,
                    values.shopifyProductVariant,
                    values.condition,
                    values.category,
                    findItemMethod
                  );
              }}
            </OnChange>
            <FormSpy
              onChange={updateRemoteStoreWithFormValues}
              subscription={{ values: true, dirty: true, active: true, dirtyFields: true }}
            />
          </Form>
        );
      }}
    />
  );
};

export default ItemDetailsForm;
